import { FC } from 'react';
import {
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Tile,
} from 'melp-design/components';
import { Divider, Skeleton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'utils/navigation';
import {
  transactionsFiltersSchema,
  useExportToXls,
  useTransactions,
} from 'store/transmission-acts';
import { formatCurrency } from 'utils/format';
import moment from 'moment';
import { useDefaultCurrency, useMe } from 'state/Administrators';
import { DownloadXlsAction } from 'components/actions';

export const TransactionsList: FC = () => {
  const { t, i18n } = useTranslation();
  const { me } = useMe();
  const { currency } = useDefaultCurrency();

  const { searchParams: filters } = useSearchParams(transactionsFiltersSchema);

  const { data: transactions, isLoading } = useTransactions(filters);
  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack direction="row" alignItems="center" gap={2}>
            {isLoading ? (
              <>
                <Skeleton variant="rounded" width={200} height={81} />
                <Skeleton variant="rounded" width={200} height={81} />
                <Skeleton variant="rounded" width={200} height={81} />
              </>
            ) : transactions ? (
              <>
                <Tile
                  title={t('transmissionActs.openingBalance.label')}
                  items={[
                    formatCurrency(
                      i18n.language,
                      transactions.meta.openingBalance,
                      currency,
                    ),
                  ]}
                />
                <Tile
                  title={t('transmissionActs.closingBalance.label')}
                  items={[
                    formatCurrency(
                      i18n.language,
                      transactions.meta.closingBalance,
                      currency,
                    ),
                  ]}
                />
              </>
            ) : null}
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <ListTotal total={transactions?.total} />

            <Stack direction="row" alignItems="center" gap={1}>
              <DownloadXlsAction
                listLength={transactions?.total ?? 0}
                exportToExcel={() =>
                  exportToXls({
                    params: {
                      ongoingBalance: true,
                      companyId:
                        me?.parentCompany.settings.depositManagementMethod ===
                        'company'
                          ? filters.company
                          : undefined,
                    },
                  })
                }
                loading={isExportingToXls}
              />
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={transactions?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'date',
            header: t('common.date'),
            cell: ({ row: { original: transaction } }) => {
              return transaction.type === 'deposit'
                ? moment(transaction.date).format('l')
                : null;
            },
          },
          {
            id: 'source',
            header: t('wallet.employeeWalletFundsSource'),
            cell: ({ row: { original: transaction } }) => {
              return transaction.type === 'expense'
                ? `${transaction.title} (${transaction.count})`
                : transaction.title;
            },
          },
          {
            id: 'amount',
            header: t('employees.amount'),
            cell: ({ row: { original: transaction } }) => {
              return formatCurrency(
                i18n.language,
                transaction.amount,
                transaction.currency,
              );
            },
          },
          {
            id: 'balance',
            header: t('transmissionActs.balance.label'),
            cell: ({ row: { original: transaction } }) => {
              return formatCurrency(
                i18n.language,
                transaction.balance,
                transaction.currency,
              );
            },
          },
        ]}
      />
    </TableWrapper>
  );
};
